/**
 * Phase 48 — Profitability Analysis API client
 * All monetary values in Minor Units (integers).
 * Percentage margins in basis points (bps). Display: (bps / 100).toFixed(2) + "%"
 * SUPER_ADMIN only.
 */
import { api } from "./client";

// ── Types ────────────────────────────────────────────────────────────────────

export interface ProductProfitRow {
  product_id: string;
  product_name: string;
  product_code: string | null;
  product_class: number;
  invoice_count: number;
  revenue_minor: number;
  cogs_minor: number;
  gross_profit_minor: number;
  /** Basis points: (bps / 100).toFixed(2) + "%" */
  gross_margin_bps: number;
}

export interface CustomerProfitRow {
  party_id: string;
  customer_name: string;
  customer_code: string | null;
  invoice_count: number;
  revenue_minor: number;
  cogs_minor: number;
  gross_profit_minor: number;
  gross_margin_bps: number;
  avg_invoice_minor: number;
}

export interface SalespersonProfitRow {
  salesperson_id: string;
  salesperson_name: string;
  invoice_count: number;
  revenue_minor: number;
  cogs_minor: number;
  gross_profit_minor: number;
  gross_margin_bps: number;
  avg_invoice_minor: number;
}

export interface ProfitSummary {
  revenue_minor: number;
  cogs_minor: number;
  gross_profit_minor: number;
  gross_margin_bps: number;
}

export interface BreakEvenData {
  year: number;
  revenue_minor: number;
  cogs_minor: number;
  gross_profit_minor: number;
  gross_margin_bps: number;
  fixed_costs_minor: number;
  variable_costs_minor: number;
  vc_ratio_bps: number;
  contribution_margin_bps: number;
  break_even_revenue_minor: number | null;
  margin_of_safety_minor: number | null;
  margin_of_safety_bps: number | null;
  has_classification_data: boolean;
}

export interface ExpenseClassificationRow {
  account_id: string;
  name_ar: string;
  name_en: string | null;
  code: string | null;
  /** -1 = unclassified, 0 = Fixed, 1 = Variable, 2 = Semi-variable */
  cost_type: number;
  notes: string | null;
}

export interface MonthlyTrendRow {
  month_label: string;
  month_yyyymm: string;
  revenue_minor: number;
  cogs_minor: number;
  gross_profit_minor: number;
  invoice_count: number;
  gross_margin_bps: number;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Format basis points as a percentage string: 3250 → "32.50%" */
export function bpsToPercent(bps: number): string {
  return (bps / 100).toFixed(2) + "%";
}

/** Format Minor Units as currency string (EGP): 1250000 → "12,500.00" */
export function minorToDisplay(minor: number, decimals = 2): string {
  const val = minor / 100;
  return val.toLocaleString("ar-EG", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// ── API functions ────────────────────────────────────────────────────────────

export interface ProfitParams {
  start?: string; // YYYY-MM or DD/MM/YYYY
  end?: string;
  limit?: number;
}

export async function getProductProfitability(params: ProfitParams = {}) {
  const qs = new URLSearchParams();
  if (params.start) qs.set("start", params.start);
  if (params.end) qs.set("end", params.end);
  if (params.limit) qs.set("limit", String(params.limit));
  const q = qs.toString() ? `?${qs}` : "";
  return api.get<{
    ok: boolean;
    data: ProductProfitRow[];
    summary: ProfitSummary & { products_count: number };
  }>(`/v1/profitability/by-product${q}`);
}

export async function getCustomerProfitability(params: ProfitParams = {}) {
  const qs = new URLSearchParams();
  if (params.start) qs.set("start", params.start);
  if (params.end) qs.set("end", params.end);
  if (params.limit) qs.set("limit", String(params.limit));
  const q = qs.toString() ? `?${qs}` : "";
  return api.get<{
    ok: boolean;
    data: CustomerProfitRow[];
    summary: ProfitSummary & { customers_count: number };
  }>(`/v1/profitability/by-customer${q}`);
}

export async function getSalespersonProfitability(params: ProfitParams = {}) {
  const qs = new URLSearchParams();
  if (params.start) qs.set("start", params.start);
  if (params.end) qs.set("end", params.end);
  if (params.limit) qs.set("limit", String(params.limit));
  const q = qs.toString() ? `?${qs}` : "";
  return api.get<{
    ok: boolean;
    data: SalespersonProfitRow[];
    summary: ProfitSummary & { salespersons_count: number };
  }>(`/v1/profitability/by-salesperson${q}`);
}

export async function getBreakEvenAnalysis(year: number) {
  return api.get<{ ok: boolean; data: BreakEvenData }>(
    `/v1/profitability/break-even?year=${year}`,
  );
}

export async function getExpenseClassifications() {
  return api.get<{ ok: boolean; data: ExpenseClassificationRow[] }>(
    "/v1/profitability/expense-classification",
  );
}

export async function upsertExpenseClassification(
  accountId: string,
  costType: number,
  notes?: string,
) {
  return api.put<{ ok: boolean }>(
    `/v1/profitability/expense-classification/${accountId}`,
    { cost_type: costType, notes },
  );
}

export async function deleteExpenseClassification(accountId: string) {
  return api.delete<{ ok: boolean }>(
    `/v1/profitability/expense-classification/${accountId}`,
  );
}

export async function getMonthlyTrend(year: number) {
  return api.get<{ ok: boolean; data: MonthlyTrendRow[]; year: number }>(
    `/v1/profitability/monthly-trend?year=${year}`,
  );
}

// ── Phase 51: Cost Centre P&L ─────────────────────────────────────────────────

export interface CostCentreRow {
  centre_id: string;
  centre_name: string;
  centre_name_en: string | null;
  code: string | null;
  is_unallocated: boolean;
  revenue_minor: number;
  expense_minor: number;
  net_profit_minor: number;
  net_margin_bps: number;
  revenue_share_bps: number;
}

export interface CostCentrePLSummary {
  revenue_minor: number;
  expense_minor: number;
  net_profit_minor: number;
  net_margin_bps: number;
  centres_count: number;
  has_unallocated: boolean;
}

export interface CostCentreAccountLine {
  account_id: string;
  account_code: string;
  name_i18n_key: string;
  custom_name: string | null;
  account_type: number;
  total_debit_minor: number;
  total_credit_minor: number;
  net_minor: number;
}

export interface CostCentrePLDetail {
  centre_id: string;
  centre_name: string;
  is_unallocated: boolean;
  revenue_lines: CostCentreAccountLine[];
  expense_lines: CostCentreAccountLine[];
  summary: {
    revenue_minor: number;
    expense_minor: number;
    net_profit_minor: number;
    net_margin_bps: number;
  };
}

export interface CostCentreInfo {
  center_id: string;
  name_ar: string;
  name_en: string | null;
  code: string | null;
  is_active: number;
}

export async function getCostCentres() {
  return api.get<{ ok: boolean; data: CostCentreInfo[] }>(
    "/v1/profitability/cost-centres",
  );
}

export async function getCostCentrePL(params: ProfitParams = {}) {
  const qs = new URLSearchParams();
  if (params.start) qs.set("start", params.start);
  if (params.end) qs.set("end", params.end);
  const q = qs.toString() ? `?${qs}` : "";
  return api.get<{
    ok: boolean;
    data: CostCentreRow[];
    summary: CostCentrePLSummary;
  }>(`/v1/profitability/by-cost-centre${q}`);
}

export async function getCostCentrePLDetail(
  centreId: string,
  params: ProfitParams = {},
) {
  const qs = new URLSearchParams();
  if (params.start) qs.set("start", params.start);
  if (params.end) qs.set("end", params.end);
  const q = qs.toString() ? `?${qs}` : "";
  return api.get<{ ok: boolean; data: CostCentrePLDetail }>(
    `/v1/profitability/by-cost-centre/${centreId}${q}`,
  );
}
